import { ArrowLeft, Building2, Calendar, DollarSign, TrendingUp, Edit3, Package, ShieldCheck } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import InfoHint from '../ui/info-hint';

/**
 * PartHeader - Redesigned part header with clickable badges
 * 
 * Shows:
 * - Part number and description (prominent)
 * - Clickable badges for OEM, Segment, Status
 * - Key metrics: SOP, EOP, Take Rate, Annual Revenue
 */

const BUSINESS_STATUSES = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'Target', label: 'Target', color: 'bg-gray-100 text-gray-700' },
  { value: 'RFQ', label: 'RFQ', color: 'bg-blue-100 text-blue-700' },
  { value: 'Quoted', label: 'Quoted', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'Nominated', label: 'Nominated', color: 'bg-purple-100 text-purple-700' },
  { value: 'Awarded', label: 'Awarded', color: 'bg-green-100 text-green-700' },
  { value: 'Development', label: 'Development', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'Pre-Production', label: 'Pre-Production', color: 'bg-orange-100 text-orange-700' },
  { value: 'Production', label: 'Production', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'End of Life', label: 'End of Life', color: 'bg-red-100 text-red-700' },
  { value: 'Lost', label: 'Lost', color: 'bg-gray-200 text-gray-600' },
];

export default function PartHeader({ 
  part, 
  onEdit,
  onBack,
  backLabel = 'Back to Parts',
  selectedProgramContext = null,
  metrics = {}
}) {
  if (!part) return null;

  const contextStatus = selectedProgramContext?.program_status;
  const currentStatus = contextStatus
    ? BUSINESS_STATUSES.find(s => s.value === contextStatus) || { value: contextStatus, label: contextStatus, color: 'bg-blue-100 text-blue-700' }
    : null;

  // Format numbers
  const formatCurrency = (cents) => {
    if (!cents) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const formatNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed.toLocaleString('en-US') : '-';
  };

  const metricCards = [
    {
      label: selectedProgramContext ? 'Market Lifecycle Vol.' : 'Committed Volume',
      value: formatNumber(metrics.lifecycleVolume || part.annual_volume_commitment),
      icon: Package,
      subtext: selectedProgramContext ? 'linked IHS/S&P basis' : 'part master',
      info: selectedProgramContext
        ? 'Total vehicle volume across the program life, summed from the linked IHS/S&P market forecast. This is the market basis, before take rate and content per vehicle.'
        : 'Volume commitment recorded on the Part Master. Select a program context to see the market-forecast basis.'
    },
    {
      label: selectedProgramContext ? 'Annual Planned Vol.' : 'Annual Volume',
      value: formatNumber(metrics.annualVolume || part.annual_volume_commitment),
      icon: TrendingUp,
      subtext: selectedProgramContext ? 'active planning basis' : 'master commitment',
      info: selectedProgramContext
        ? 'Your planned units for the headline year = market volume x take rate x content per vehicle (+ any adjustment), from the active scenario.'
        : 'Annual volume from the Part Master. Program-specific planning appears once a program context is selected.'
    },
    {
      label: 'Weighted Price',
      value: metrics.weightedPrice || formatCurrency(part.awarded_price_cents || part.quoted_price_cents || part.target_price_cents),
      icon: DollarSign,
      subtext: metrics.priceSource || 'master schedule',
      info: 'Your quoted price per unit. The label below shows which price is active (awarded, quoted, or target). This is the price the S&OP grid uses for revenue.'
    },
    {
      label: 'Forecast Links',
      value: formatNumber(metrics.forecastLinks ?? part.ihs_links_count),
      icon: ShieldCheck,
      subtext: selectedProgramContext ? 'applied to context' : 'global/master links',
      info: selectedProgramContext
        ? 'Number of IHS/S&P carline forecasts linked as the market basis for this part in this program.'
        : 'Total IHS/S&P carline forecasts linked to this Part Master across all programs.'
    }
  ];

  return (
    <div className="bg-white border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onBack}
          className="mb-4 -ml-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {backLabel}
        </Button>

        {/* Main header row */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
              {selectedProgramContext ? 'Part 360 / Program Context' : 'Part 360 / Part Master'}
            </div>

            {/* Part Number */}
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {part.part_number}
              </h1>
              
              {/* System ID Badge */}
              {part.system_id && (
                <Badge variant="outline" className="text-xs font-mono text-gray-500 border-gray-300">
                  {part.system_id}
                </Badge>
              )}
              
              {currentStatus && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${currentStatus.color}`}>
                  {currentStatus.label}
                </span>
              )}
            </div>

            {/* Description */}
            <div className="mb-4">
              <p className="text-lg text-gray-600">{part.description || 'No description set'}</p>
            </div>

            {/* Clickable Badges Row */}
            <div className="flex items-center flex-wrap gap-3">
              <Badge variant="secondary" className="inline-flex items-center gap-1.5 text-sm font-medium">
                <Building2 className="h-3.5 w-3.5" />
                {selectedProgramContext?.program_oem || part.oem || 'OEM not set'}
              </Badge>

              {(part.segment_name || part.sub_segment_name) && (
                <Badge variant="outline" className="text-sm font-medium">
                  {[part.segment_name, part.sub_segment_name].filter(Boolean).join(' / ')}
                </Badge>
              )}

              {/* Vehicle program is program-specific, not part identity — shown via the context badge below */}
              {selectedProgramContext && (
                <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
                  Viewing in {selectedProgramContext.program_name}
                </Badge>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={onEdit}>
            <Edit3 className="h-4 w-4" />
            Edit Part
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-gray-500 text-xs font-medium mb-1">
              <Calendar className="h-3.5 w-3.5" />
              SOP / EOP
              <InfoHint title="SOP / EOP" size={12}>
                {selectedProgramContext
                  ? 'Start and end of production for this program, from the program record.'
                  : 'Start and end of production from the Part Master.'}
              </InfoHint>
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {formatDate(selectedProgramContext?.program_sop || part.sop_date)} / {formatDate(selectedProgramContext?.program_eop || part.eop_date)}
            </div>
          </div>

          {metricCards.map(({ label, value, icon: Icon, subtext, info }) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-gray-500 text-xs font-medium mb-1">
                <Icon className="h-3.5 w-3.5" />
                {label}
                {info && <InfoHint title={label} size={12}>{info}</InfoHint>}
              </div>
              <div className="text-lg font-semibold text-gray-900">{value}</div>
              <div className="mt-1 text-xs text-gray-500">{subtext}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
